import React from 'react';
import { Search } from 'lucide-react';
import type { ComponentType } from '../../schemas/validation';
import type { ComponentOption } from './componentOptions';

interface TemplatesTabProps {
  templatesByCategory: Record<string, ComponentOption[]>;
  isLoading: boolean;
  onSelectTemplate: (componentType: ComponentType) => void;
}

export const TemplatesTab: React.FC<TemplatesTabProps> = ({
  templatesByCategory,
  isLoading,
  onSelectTemplate,
}) => {
  if (Object.keys(templatesByCategory).length === 0) {
    return (
      <div className="text-center py-12">
        <Search size={48} className="mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
        <p className="text-gray-500">Try adjusting your search terms or filters</p>
      </div>
    );
  }

  return (
    <>
      {Object.entries(templatesByCategory).map(([category, components]) => (
        <div key={category} className="mb-6 last:mb-0">
          {/* Category Header */}
          <div className="flex items-center mb-4">
            <h3 className="text-sm font-medium text-gray-900 uppercase tracking-wide">
              {category}
            </h3>
            <div className="flex-1 h-px bg-gray-200 ml-4"></div>
          </div>

          {/* Components Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {components.map(component => {
              const IconComponent = component.icon;
              const colors = component.colorScheme;
              return (
                <button
                  key={component.type}
                  onClick={() => onSelectTemplate(component.type)}
                  disabled={isLoading}
                  className={`group relative ${colors.bg} border border-gray-200 rounded-lg p-4 text-left ${colors.hoverBorder} ${colors.hoverBg} transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
                >
                  {/* Component Icon */}
                  <div
                    className={`flex items-center justify-center w-10 h-10 ${colors.iconBg} rounded-lg mb-3 transition-colors`}
                  >
                    <IconComponent
                      size={20}
                      className={`${colors.iconColor} ${colors.hoverIcon}`}
                    />
                  </div>

                  {/* Component Info */}
                  <h4 className={`text-sm font-medium text-gray-900 mb-1 ${colors.hoverText}`}>
                    {component.name}
                  </h4>
                  <p className={`text-xs text-gray-500 leading-relaxed ${colors.hoverDescription}`}>
                    {component.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
};
